// ClientEmailsSection — emailova komunikace v ClientDetail.
//
// Sekce zobrazuje vlakna z Gmail API (info@/klienti@/mereni@zateckal.cz)
// kde figuruje email klienta jako From nebo To.
//
// - Lazy fetch on expand (useState 'opened')
// - Pres /api/client-emails endpoint (multi-account agregace)
// - Kliknuti na vlakno → expand, nacte detail z /api/client-email-thread
// - Reply form (vytvori draft pres /api/client-email-reply, otevre Gmail)

import { confirmDialog } from "./confirmHost.jsx";
import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Mail, RefreshCw, AlertTriangle, ChevronRight, ChevronDown, ExternalLink, Send, X, Paperclip, Download, Bold, Italic, Link as LinkIcon, List, FileText, Search, Filter, Inbox, Calendar } from "lucide-react";
import { formatDate, formatDateTime } from "../lib/format.js";
import { useUzkaObrazovka } from "../lib/obrazovka.js";

// Barevne mapy per Gmail ucet (avatar + badge). 21.5.2026: rozsireno o avatar gradient,
// ring barvy a darker odstin pro hover state.
const ACCOUNT_COLORS = {
  info: {
    bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200",
    avatarBg: "bg-gradient-to-br from-blue-500 to-blue-700", ring: "ring-blue-200",
    label: "info",
  },
  klienti: {
    bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200",
    avatarBg: "bg-gradient-to-br from-emerald-500 to-emerald-700", ring: "ring-emerald-200",
    label: "klienti",
  },
  mereni: {
    bg: "bg-brand-100", text: "text-brand-700", border: "border-brand-200",
    avatarBg: "bg-gradient-to-br from-brand-500 to-brand-700", ring: "ring-brand-200",
    label: "mereni",
  },
};

// Mozne date range pro filter ?since=Nd. null = vse, jinak pocet dnu.
const DATE_RANGES = [
  { key: "all", label: "Vše", days: null },
  { key: "30", label: "30 dní", days: 30 },
  { key: "90", label: "90 dní", days: 90 },
  { key: "365", label: "1 rok", days: 365 },
];

function formatEmailDate(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" });
  return formatDate(d);
}

function stripEmailQuotes(text) {
  if (!text) return "";
  // Odebrat "> " citation lines z reply
  return text.split("\n").filter((l) => !l.trimStart().startsWith(">")).join("\n").trim();
}

// sessionStorage cache 5 minut — pri re-open klienta se prvni vykresleni hned
// z cache + background refresh. Vymeni viditelnou cekani na Gmail API per klient.
// 21.5.2026: klic obsahuje i :since suffix aby ruzne date range nepoluovaly cache.
const EMAILS_CACHE_KEY = "fw40_client_emails_v1";
const EMAILS_CACHE_TTL_MS = 5 * 60 * 1000;
// Per-thread cache (sdilena napric klienty) — klik na thread = instant z cache.
const THREAD_CACHE_KEY = "fw40_email_thread_v1";
const THREAD_CACHE_TTL_MS = 10 * 60 * 1000;
// 21.5.2026 memory safety:
// - THREAD_CACHE_MAX_ENTRIES: LRU cap (eviction nejstarsi pri prekroceni).
// - THREAD_CACHE_MAX_DETAIL_BYTES: skip caching velkych HTML emailu (>200KB JSON).
//   sessionStorage limit je ~5-10MB, jeden gigantic email by ho prejel.
const THREAD_CACHE_MAX_ENTRIES = 30;
const THREAD_CACHE_MAX_DETAIL_BYTES = 200 * 1024;

function buildEmailCacheKey(email, sinceDays) {
  return `${email}:${sinceDays || "all"}`;
}

function readEmailsCache(email, sinceDays) {
  try {
    const all = JSON.parse(sessionStorage.getItem(EMAILS_CACHE_KEY) || "{}");
    const entry = all[buildEmailCacheKey(email, sinceDays)];
    if (!entry || Date.now() - entry.at > EMAILS_CACHE_TTL_MS) return null;
    return entry.data;
  } catch { return null; }
}

function writeEmailsCache(email, sinceDays, data) {
  try {
    const all = JSON.parse(sessionStorage.getItem(EMAILS_CACHE_KEY) || "{}");
    all[buildEmailCacheKey(email, sinceDays)] = { at: Date.now(), data };
    sessionStorage.setItem(EMAILS_CACHE_KEY, JSON.stringify(all));
  } catch { /* full or SSR */ }
}

function readThreadCache(threadId) {
  try {
    const all = JSON.parse(sessionStorage.getItem(THREAD_CACHE_KEY) || "{}");
    const entry = all[threadId];
    if (!entry || Date.now() - entry.at > THREAD_CACHE_TTL_MS) return null;
    return entry.detail;
  } catch { return null; }
}

function writeThreadCache(threadId, detail) {
  try {
    // Skip velke HTML emaily (preventivne proti quota fill — sessionStorage ~5-10MB).
    const encoded = JSON.stringify(detail);
    if (encoded.length > THREAD_CACHE_MAX_DETAIL_BYTES) return;

    let all = {};
    try { all = JSON.parse(sessionStorage.getItem(THREAD_CACHE_KEY) || "{}"); } catch { /* corrupted */ }
    all[threadId] = { at: Date.now(), detail };

    // LRU cap — pokud presahujeme limit, drop nejstarsi.
    const entries = Object.entries(all);
    if (entries.length > THREAD_CACHE_MAX_ENTRIES) {
      entries.sort((a, b) => b[1].at - a[1].at); // newest first
      all = Object.fromEntries(entries.slice(0, THREAD_CACHE_MAX_ENTRIES));
    }
    sessionStorage.setItem(THREAD_CACHE_KEY, JSON.stringify(all));
  } catch (e) {
    // Quota exceeded — drop 50% nejstarsich a zkus zaznam jeste jednou.
    try {
      let all = JSON.parse(sessionStorage.getItem(THREAD_CACHE_KEY) || "{}");
      const entries = Object.entries(all);
      entries.sort((a, b) => b[1].at - a[1].at);
      const keep = Math.floor(entries.length / 2);
      all = Object.fromEntries(entries.slice(0, keep));
      all[threadId] = { at: Date.now(), detail };
      sessionStorage.setItem(THREAD_CACHE_KEY, JSON.stringify(all));
    } catch { /* give up */ }
  }
}

// Background prefetch zbylych threadu (mimo prefetched z initial response).
// Chunkove po 5 paralelne — Gmail rate limit safety. Pokud thread uz je v cache, skip.
async function backgroundPrefetchThreads(threads, prefetched) {
  const toFetch = threads.filter((t) => !prefetched[t.thread_id] && !readThreadCache(t.thread_id));
  if (toFetch.length === 0) return;
  const CHUNK = 5;
  for (let i = 0; i < toFetch.length; i += CHUNK) {
    const chunk = toFetch.slice(i, i + CHUNK);
    await Promise.all(chunk.map(async (t) => {
      try {
        const r = await fetch(`/api/client-email-thread?account=${t.account}&thread_id=${t.thread_id}`, { credentials: "include" });
        if (!r.ok) return;
        const d = await r.json();
        writeThreadCache(t.thread_id, d);
      } catch { /* silent */ }
    }));
  }
}

// 21. 10. 2026 (hlášení #175): automatické e-maily rezervačního systému se v detailu
// schovávají do sbaleného balíčku, ať nepřekáží mezi rozhovory s klientkou.
// Pozná se podle předmětu ze šablon v netlify/functions/_lib/rezervaceNotifikace.cjs
// a portal-send-code.js. Když se tam změní předmět, je potřeba upravit i tento seznam.
// Vlákno s více zprávami (klientka odpověděla) zůstává vždy mezi rozhovory.
// Uvítací e-maily („Děkujeme za zájem“, „Informace ke spolupráci“) sem záměrně nepatří.
const AUTOMATICKE_PREDMETY = [
  { re: /^Zítra (trénink|konzultace):/i, typ: "Připomínka" },
  { re: /^Potvrzení (tréninku|konzultace):/i, typ: "Potvrzení" },
  { re: /^(Rezervace|Konzultace) čeká na potvrzení:/i, typ: "Žádost" },
  { re: /^Rezervace nebyla potvrzena:/i, typ: "Zamítnuto" },
  { re: /^Pravidelný trénink:/i, typ: "Pravidelný" },
  { re: /^Změna termínu (tréninku|konzultace):/i, typ: "Změna" },
  { re: /^Zrušení (tréninku|konzultace):/i, typ: "Zrušení" },
  { re: /^Trénink .+ propadá$/i, typ: "Propadnutí" },
  { re: /^Přihlašovací kód do portálu/i, typ: "Kód" },
];
const TYP_BARVY = {
  "Připomínka": "bg-plum-50 text-plum-700", "Potvrzení": "bg-plum-50 text-plum-700", "Pravidelný": "bg-plum-50 text-plum-700",
  "Žádost": "bg-amber-50 text-amber-800", "Změna": "bg-amber-50 text-amber-800",
  "Zrušení": "bg-rose-50 text-rose-700", "Propadnutí": "bg-rose-50 text-rose-700", "Zamítnuto": "bg-rose-50 text-rose-700",
  "Kód": "bg-slate-100 text-slate-600",
};
export function typAutomatickehoEmailu(t) {
  if (!t || Number(t.message_count || 1) > 1) return null;
  const predmet = String(t.subject || "").trim();
  const hit = AUTOMATICKE_PREDMETY.find((x) => x.re.test(predmet));
  return hit ? hit.typ : null;
}

export default function ClientEmailsSection({ client }) {
  const uzka = useUzkaObrazovka();
  // 21.5.2026: filter state ridi UI selekce. `since` jde na backend (ovlivnuje Gmail
  // search query), ostatni filtry probíhají client-side v na nactenem seznamu.
  const [filter, setFilter] = useState({
    search: "",
    account: "all", // "all" | "info" | "klienti" | "mereni"
    unreadOnly: false,
    sinceKey: "all", // klic z DATE_RANGES
  });
  const sinceDays = useMemo(() => {
    const r = DATE_RANGES.find((x) => x.key === filter.sinceKey);
    return r?.days || null;
  }, [filter.sinceKey]);

  // Instant render z cache, background refresh. Klic vcetne since aby ruzne date range
  // nesdilely cache vystup.
  const [data, setData] = useState(() => client?.email ? readEmailsCache(client.email, null) : null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [expandedThreadId, setExpandedThreadId] = useState(null);

  const load = useCallback(async (force = false) => {
    if (!client?.email) {
      setErr("Klient nemá uložený email v profilu.");
      return;
    }
    // Pokud mame fresh cache pro aktualni since a neforcujeme, neukazujem spinner.
    // 21.5.2026: pri zmene since neclear-ujeme data — toolbar zustava videt, spinner
    // se renderuje inline. Lepsi UX nez flash off-and-on.
    const cached = force ? null : readEmailsCache(client.email, sinceDays);
    if (cached) {
      setData(cached);
    } else {
      setLoading(true);
    }
    setErr(null);
    try {
      let url = `/api/client-emails?email=${encodeURIComponent(client.email)}`;
      if (sinceDays) url += `&since=${sinceDays}`;
      if (force) url += `&force=1`;
      const r = await fetch(url, { credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setData(d);
      writeEmailsCache(client.email, sinceDays, d);
      // Uloz prefetched thready do sessionStorage cache pro re-open
      if (d.prefetched) {
        for (const [tid, detail] of Object.entries(d.prefetched)) {
          writeThreadCache(tid, detail);
        }
      }
      // Background prefetch zbylych threadu — uzivatel uvidi instant pro vsechny klikem
      if (d.threads && d.threads.length > 0) {
        backgroundPrefetchThreads(d.threads, d.prefetched || {});
      }
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [client?.email, sinceDays]);

  // 20.5.2026 auto-load + cache: instant render z cache, fresh fetch na pozadi.
  // 21.5.2026: pri zmene since (date range filter) refetch s novym ?since= param.
  React.useEffect(() => {
    if (client?.email) load();
  }, [client?.email, sinceDays, load]);

  // Client-side filter: search, account, unreadOnly. Pocet po filteru pro infobar.
  const filteredThreads = useMemo(() => {
    if (!data?.threads) return [];
    const q = filter.search.trim().toLowerCase();
    return data.threads.filter((t) => {
      if (filter.account !== "all" && t.account !== filter.account) return false;
      if (filter.unreadOnly && !t.unread) return false;
      if (q) {
        const hay = `${t.subject || ""} ${t.snippet || ""} ${t.from || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data?.threads, filter]);

  // Rozdělení na rozhovory a automatické zprávy (balíček na konci, default sbalený).
  const [autoOtevreno, setAutoOtevreno] = useState(false);
  const [autoVse, setAutoVse] = useState(false);
  const rozhovory = useMemo(() => filteredThreads.filter((t) => !typAutomatickehoEmailu(t)), [filteredThreads]);
  const automaticke = useMemo(() => filteredThreads.filter((t) => typAutomatickehoEmailu(t)), [filteredThreads]);
  // Při hledání se balíček s nálezy ukáže rozbalený.
  const autoRozbaleno = autoOtevreno || filter.search.trim() !== "";
  const AUTO_LIMIT = 6;
  const automatickeViditelne = autoVse ? automaticke : automaticke.slice(0, AUTO_LIMIT);

  const totalCount = data?.threads?.length || 0;
  const filteredCount = filteredThreads.length;
  const isFilterActive = filter.search.trim() !== "" || filter.account !== "all" || filter.unreadOnly || filter.sinceKey !== "all";

  const resetFilter = () => setFilter({ search: "", account: "all", unreadOnly: false, sinceKey: "all" });

  if (!client?.email) {
    return (
      <div className="text-sm text-slate-500 italic px-3 py-4">
        Klient nemá v profilu email, emailovou komunikaci nejde načíst.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* 9.9.2026 — na telefonu je z dlouhé věty jen adresa a Obnovit je ikona 44 px. */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1 text-sm text-slate-600">
          {uzka ? (
            <span className="block truncate demo-sensitive">Vlákna z <span className="font-semibold text-slate-700">{client.email}</span></span>
          ) : (
            <>Vlákna z <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">{client.email}</code> napříč inboxy info / klienti / mereni</>
          )}
        </div>
        <button
          onClick={() => load(true)}
          disabled={loading}
          aria-label="Obnovit"
          title="Obnovit"
          className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-50 ${uzka ? "h-11 w-11" : "px-2.5 py-1.5 text-xs"}`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {!uzka && "Obnovit"}
        </button>
      </div>

      {err && (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
          <AlertTriangle className="w-4 h-4 inline mr-1" /> {err}
        </div>
      )}

      {data && data.warning && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
          <AlertTriangle className="w-4 h-4 inline mr-1" /> {data.warning}
        </div>
      )}

      {/* Filter toolbar — vzdy videt pokud nemame error a klient ma email.
          Pri loadingu se inline spinner v toolbaru postara o feedback. */}
      <FilterToolbar
        filter={filter}
        setFilter={setFilter}
        totalCount={totalCount}
        filteredCount={filteredCount}
        isFilterActive={isFilterActive}
        onReset={resetFilter}
        loading={loading}
      />

      {loading && !data && (
        <div className="text-sm text-slate-500 py-4 inline-flex items-center gap-2">
          <RefreshCw className="w-4 h-4 animate-spin" /> Načítám vlákna z Gmail API…
        </div>
      )}

      {!loading && data && data.threads && data.threads.length === 0 && (
        <div className="text-sm text-slate-500 italic py-4">
          Žádná emailová komunikace s tímto klientem{filter.sinceKey !== "all" ? ` (filtr: ${DATE_RANGES.find(r => r.key === filter.sinceKey)?.label})` : ""}.
        </div>
      )}

      {!loading && data && data.threads && data.threads.length > 0 && filteredThreads.length === 0 && (
        <div className="text-sm text-slate-500 italic py-4 bg-slate-50 border border-slate-200 rounded-xl px-3">
          Žádné vlákno neodpovídá zvolenému filtru. <button onClick={resetFilter} className="text-plum-600 hover:underline">Resetovat filtr</button>
        </div>
      )}

      {filteredThreads.length > 0 && (
        <div className="space-y-2">
          {rozhovory.map((t) => (
            <EmailThreadItem
              key={t.thread_id}
              thread={t}
              prefetchedDetail={data.prefetched?.[t.thread_id]}
              expanded={expandedThreadId === t.thread_id}
              onToggle={() => setExpandedThreadId(expandedThreadId === t.thread_id ? null : t.thread_id)}
            />
          ))}

          {automaticke.length > 0 && (
            <div className="rounded-xl border border-dashed border-plum-200 bg-plum-50/40">
              <button
                type="button"
                onClick={() => setAutoOtevreno(!autoOtevreno)}
                className="flex w-full min-h-[44px] items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-plum-50"
              >
                <span className={`flex flex-shrink-0 items-center justify-center rounded-full bg-plum-100 text-plum-700 ${uzka ? "h-9 w-9" : "h-10 w-10"}`}>🔔</span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-[14.5px] font-bold text-slate-800">{uzka ? "Automatické zprávy" : "Automatické zprávy o trénincích"}</span>
                    <span className="rounded-full bg-plum-600 px-2 py-0.5 text-[11.5px] font-bold text-white">{automaticke.length}</span>
                  </span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    {uzka ? "" : "Připomínky, potvrzení a změny termínů · "}poslední {formatEmailDate(automaticke[0]?.date_ts)}
                  </span>
                </span>
                {autoRozbaleno ? <ChevronDown className="h-5 w-5 flex-shrink-0 text-slate-400" /> : <ChevronRight className="h-5 w-5 flex-shrink-0 text-slate-400" />}
              </button>
              {autoRozbaleno && (
                <div className="space-y-1 border-t border-dashed border-plum-200 px-2 py-2">
                  {automatickeViditelne.map((t) => {
                    if (expandedThreadId === t.thread_id) {
                      return (
                        <EmailThreadItem
                          key={t.thread_id}
                          thread={t}
                          prefetchedDetail={data.prefetched?.[t.thread_id]}
                          expanded
                          onToggle={() => setExpandedThreadId(null)}
                        />
                      );
                    }
                    const typ = typAutomatickehoEmailu(t);
                    return (
                      <button
                        key={t.thread_id}
                        type="button"
                        onClick={() => setExpandedThreadId(t.thread_id)}
                        className="flex w-full min-h-[40px] items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] text-slate-700 hover:bg-white"
                      >
                        <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold ${TYP_BARVY[typ] || "bg-slate-100 text-slate-600"}`}>{typ}</span>
                        <span className={`min-w-0 flex-1 truncate ${t.unread ? "font-bold" : ""}`}>{t.subject}</span>
                        <span className="flex-shrink-0 whitespace-nowrap text-xs text-slate-500">{formatEmailDate(t.date_ts)}</span>
                      </button>
                    );
                  })}
                  {automaticke.length > AUTO_LIMIT && (
                    <button
                      type="button"
                      onClick={() => setAutoVse(!autoVse)}
                      className="w-full min-h-[40px] rounded-lg py-1.5 text-center text-[13px] font-semibold text-plum-700 hover:bg-white"
                    >
                      {autoVse ? "Zobrazit méně" : `Zobrazit další ${automaticke.length - AUTO_LIMIT}`}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// FilterToolbar — search input + account pilly + unread toggle + date range dropdown.
// Vse propojeno se state v ClientEmailsSection. Date range jde na backend pres since param.
function FilterToolbar({ filter, setFilter, totalCount, filteredCount, isFilterActive, onReset, loading }) {
  const uzka = useUzkaObrazovka();
  const [otevreno, setOtevreno] = useState(false);
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-2.5 space-y-2.5">
      {/* Radek 1: search input + reset */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            placeholder={uzka ? "Hledat…" : "Hledat v předmětu, obsahu, odesílateli..."}
            className="field w-full min-h-[44px] pl-8 pr-3 sm:min-h-0 sm:rounded-lg sm:border sm:border-slate-300"
          />
          {filter.search && (
            <button
              onClick={() => setFilter({ ...filter, search: "" })}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              title="Smazat hledání"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {isFilterActive && (
          <button
            onClick={onReset}
            className="btn btn-secondary whitespace-nowrap"
            title="Resetovat všechny filtry"
          >
            <X className="w-3.5 h-3.5" /> Reset
          </button>
        )}
      </div>

      {/* 9.9.2026 — na telefonu jsou filtry pod řádkem "Filtry" i s odznaky, co je zapnuté. */}
      {uzka && (
        <button
          type="button"
          onClick={() => setOtevreno((v) => !v)}
          className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-xl border-2 border-slate-200 bg-white px-3"
        >
          <span className="flex items-center gap-2 text-[13.5px] font-bold text-slate-700"><Filter className="h-4 w-4 text-slate-400" /> Filtry</span>
          <span className="flex flex-wrap items-center justify-end gap-1.5">
            {filter.account !== "all" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11.5px] font-bold text-slate-600">{filter.account}</span>}
            {filter.unreadOnly && <span className="rounded-full bg-plum-100 px-2 py-0.5 text-[11.5px] font-bold text-plum-700">nepřečtené</span>}
            <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[11.5px] font-bold text-brand-800">{DATE_RANGES.find((r) => r.key === filter.sinceKey)?.label || ""}</span>
            <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${otevreno ? "rotate-180" : ""}`} />
          </span>
        </button>
      )}
      {/* Radek 2: account pilly + unread toggle + date dropdown */}
      <div className={`${uzka && !otevreno ? "hidden" : "flex"} flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-1.5`}>
        {/* Account pilly */}
        <div className="inline-flex w-full items-center overflow-hidden rounded-xl border border-slate-300 bg-white sm:w-auto sm:rounded-lg">
          {[{ k: "all", l: "Vše" }, { k: "info", l: "info" }, { k: "klienti", l: "klienti" }, { k: "mereni", l: "mereni" }].map((opt, idx) => {
            const active = filter.account === opt.k;
            const colors = ACCOUNT_COLORS[opt.k];
            return (
              <button
                key={opt.k}
                onClick={() => setFilter({ ...filter, account: opt.k })}
                className={`min-h-[40px] flex-1 px-2.5 py-1 text-xs font-medium transition-colors sm:min-h-0 sm:flex-none ${idx > 0 ? "border-l border-slate-300" : ""} ${
                  active
                    ? opt.k === "all" ? "bg-slate-700 text-white" : `${colors?.bg || "bg-slate-200"} ${colors?.text || "text-slate-800"}`
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {opt.l}
              </button>
            );
          })}
        </div>

        {/* Unread toggle */}
        <button
          onClick={() => setFilter({ ...filter, unreadOnly: !filter.unreadOnly })}
          className={`inline-flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-xl border px-2.5 py-1 text-xs font-medium transition-colors sm:min-h-0 sm:w-auto sm:rounded-lg ${
            filter.unreadOnly
              ? "bg-plum-600 border-plum-600 text-white"
              : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
          }`}
          title="Zobrazit pouze nepřečtená vlákna"
        >
          <Inbox className="w-3.5 h-3.5" /> Nepřečtené
        </button>

        {/* Date range dropdown */}
        <div className="inline-flex w-full items-center overflow-hidden rounded-xl border border-slate-300 bg-white sm:w-auto sm:rounded-lg">
          <span className="inline-flex min-h-[40px] items-center gap-1 border-r border-slate-300 bg-slate-50 px-2 py-1 text-xs text-slate-500 sm:min-h-0">
            <Calendar className="w-3.5 h-3.5" /> Období
          </span>
          <select
            value={filter.sinceKey}
            onChange={(e) => setFilter({ ...filter, sinceKey: e.target.value })}
            disabled={loading}
            className="min-h-[40px] flex-1 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none disabled:opacity-50 sm:min-h-0 sm:flex-none"
          >
            {DATE_RANGES.map((r) => (
              <option key={r.key} value={r.key}>{r.label}</option>
            ))}
          </select>
        </div>

        {/* Spacer + counter */}
        <div className="hidden flex-1 sm:block" />
        <div className="inline-flex items-center justify-end gap-1.5 whitespace-nowrap text-xs text-slate-500">
          {loading && <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          {isFilterActive ? (
            <>Zobrazeno <strong className="text-slate-700">{filteredCount}</strong> z {totalCount}</>
          ) : (
            <><strong className="text-slate-700">{totalCount}</strong> {totalCount === 1 ? "vlákno" : totalCount < 5 ? "vlákna" : "vláken"}</>
          )}
        </div>
      </div>
    </div>
  );
}

// Komponenta jednoho vlakna — header + on-expand load detail.
// 20.5.2026: priorita zdroju (vsechny instant):
//   1. prefetchedDetail (server prefetchnul top 5 v initial response)
//   2. sessionStorage thread cache (background prefetch zbylych threadu po loaded listu)
//   3. fetch (fallback — jen pokud cache miss + prefetch nestihl)
function EmailThreadItem({ thread, prefetchedDetail, expanded, onToggle }) {
  const uzka = useUzkaObrazovka();
  // Initial: server prefetch → cache → null
  const [detail, setDetail] = useState(() => prefetchedDetail || readThreadCache(thread.thread_id) || null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);
  // 21.5.2026 smart collapse: per-message expand state. Pri dlouhem vlakne (>3 messages)
  // default expand pouze prvni + posledni 2 message. Ostatni jako outline (header + snippet).
  // Set obsahuje IDs explicitne rozbalenych messages. expandAll = true overrideuje.
  const [expandedMsgIds, setExpandedMsgIds] = useState(new Set());
  const [expandAll, setExpandAll] = useState(false);
  const colors = ACCOUNT_COLORS[thread.account] || ACCOUNT_COLORS.info;

  // Reset per-message state pri zmene vlakna
  React.useEffect(() => {
    setExpandedMsgIds(new Set());
    setExpandAll(false);
  }, [thread.thread_id]);

  // Helper: ktere message rendrovat jako expanded.
  // 21.5.2026: collapse aktivni pro vlakna >1 zprava. Default expanded JEN prvni zprava
  // (start konverzace), vse ostatni sbalene. User kliknutim rozbali co potrebuje, nebo
  // pouzije Rozbalit vse pro cely kontext.
  const isLongThread = (detail?.messages?.length || 0) > 1;
  const defaultExpandedIndexes = useMemo(() => {
    if (!detail?.messages || detail.messages.length === 0) return new Set();
    return new Set([0]); // jen prvni zprava expanded, vsechny dalsi collapsed
  }, [detail?.messages]);

  const isMsgExpanded = (idx, msgId) => {
    if (expandAll) return true;
    if (expandedMsgIds.has(msgId)) return true;
    return defaultExpandedIndexes.has(idx);
  };

  const toggleMsg = (msgId) => {
    setExpandedMsgIds((prev) => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  };

  React.useEffect(() => {
    if (!expanded || detail) return;
    // Pred fetch jeste check cache — background prefetch ho mohl mezitim doplnit
    const cached = readThreadCache(thread.thread_id);
    if (cached) { setDetail(cached); return; }
    setLoading(true); setErr(null);
    fetch(`/api/client-email-thread?account=${thread.account}&thread_id=${thread.thread_id}`, { credentials: "include" })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d.error || "Fetch failed");
        setDetail(d);
        writeThreadCache(thread.thread_id, d);
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [expanded, detail, thread.account, thread.thread_id]);

  // 21.5.2026 redesign: avatar pro account, vetsi fonty, 2-line snippet (videt o cem to je),
  // unread = tlusty levy border + indigo bg. Hover lift effect.
  return (
    <div
      className={`rounded-xl border transition-all overflow-hidden ${
        thread.unread
          ? "border-plum-300 bg-plum-50/40 border-l-4 border-l-plum-500"
          : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm"
      }`}
    >
      {/* Header — clickable. 9.9.2026: na telefonu bez avataru, ať zbyde šířka na text. */}
      <button
        onClick={onToggle}
        className="w-full text-left px-3 py-3 flex items-start gap-3 hover:bg-slate-50/70 rounded-xl min-h-[44px] sm:min-h-0"
      >
        {/* Avatar kruh s ikonkou Mail v barve uctu */}
        {uzka ? (
          thread.unread ? <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-plum-600" title="Nepřečtené" /> : null
        ) : (
          <div className={`flex-shrink-0 w-10 h-10 rounded-full ${colors.avatarBg} flex items-center justify-center text-white shadow-sm ring-2 ${colors.ring} relative`}>
            <Mail className="w-5 h-5" />
            {thread.unread && (
              <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-plum-600 rounded-full border-2 border-white" title="Nepřečtené" />
            )}
          </div>
        )}

        {/* Obsah */}
        <div className="flex-1 min-w-0">
          {/* Radek 1: subject + datum. Na telefonu má předmět dva řádky a datum
              se přesune mezi odznaky pod něj (9.9.2026). */}
          <div className="flex items-baseline gap-2 mb-0.5">
            <span
              className={`flex-1 text-[14.5px] font-bold leading-snug sm:truncate sm:text-base sm:font-semibold ${thread.unread ? "text-slate-900" : "text-slate-800"}`}
              style={uzka ? { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" } : undefined}
            >
              {thread.subject}
            </span>
            {!uzka && <span className="text-xs text-slate-500 whitespace-nowrap flex-shrink-0">{formatEmailDate(thread.date_ts)}</span>}
          </div>

          {/* Radek 2: From + account badge + message count */}
          <div className="mb-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-600 sm:gap-2">
            <span className={`px-1.5 py-0.5 rounded-full font-medium text-[11px] ${colors.bg} ${colors.text}`}>
              {thread.account}
            </span>
            {uzka && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500">{formatEmailDate(thread.date_ts)}</span>}
            {!uzka && <span className="truncate flex-1 min-w-0">{thread.from}</span>}
            <span className={uzka ? "rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500" : "text-slate-400 whitespace-nowrap flex-shrink-0"}>
              {thread.message_count} {thread.message_count === 1 ? "zpráva" : thread.message_count < 5 ? "zprávy" : "zpráv"}
            </span>
          </div>

          {/* Radek 3: snippet 2 radky */}
          <div
            className="text-sm text-slate-600 leading-snug"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
            }}
          >
            {thread.snippet || <span className="italic text-slate-400">(prázdný snippet)</span>}
          </div>
        </div>

        {/* Chevron vpravo (na telefonu ukazuje dolů, karta se rozbaluje pod sebe) */}
        <div className="flex-shrink-0 mt-1">
          {expanded
            ? <ChevronDown className="w-5 h-5 text-slate-400" />
            : (uzka ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />)}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-slate-200 px-3 py-3 space-y-3 bg-slate-50/30">
          {loading && <div className="text-sm text-slate-500"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" /> Načítám zprávy…</div>}
          {err && <div className="text-sm text-rose-700">{err}</div>}

          {/* 21.5.2026 smart collapse: pri vlakne >1 zpravy ukaz outline + tlacitka Rozbalit/Sbalit vse */}
          {detail && isLongThread && (
            <div className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600">
              <div className="inline-flex items-center gap-1.5">
                <Inbox className="w-3.5 h-3.5 text-plum-500" />
                <span>
                  <strong className="text-slate-800">{detail.messages.length}</strong> zpráv ve vlákně.
                  Klikni na sbalenou zprávu pro rozbalení, nebo Rozbalit vše pro celý kontext.
                </span>
              </div>
              <button
                onClick={() => { setExpandAll(!expandAll); setExpandedMsgIds(new Set()); }}
                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded inline-flex items-center gap-1 whitespace-nowrap font-medium"
              >
                {expandAll ? <><ChevronDown className="w-3.5 h-3.5" /> Sbalit vše</> : <><ChevronRight className="w-3.5 h-3.5" /> Rozbalit vše</>}
              </button>
            </div>
          )}

          {detail && detail.messages && detail.messages.map((msg, idx) => (
            <MessageView
              key={msg.id}
              msg={msg}
              account={thread.account}
              collapsed={!isMsgExpanded(idx, msg.id)}
              onToggleCollapse={() => toggleMsg(msg.id)}
              isCollapsibleContext={isLongThread}
            />
          ))}

          {/* 21.5.2026: Otevřít v Gmailu button odstranen - URL formaty selhavaji
              s Workspace alias konfiguracemi (info@/klienti@ pres jeden inbox), uzivatel
              ne vzdy ma prihlaseny ten konkretni Gmail v session. Misto toho mame
              full UX (cteni + odpoved) primo v admin. */}
          {/* 9.9.2026 — odpovídat jde jen z počítače, psaní delšího e-mailu na telefonu
              v malém editoru nedává smysl. Na telefonu je místo tlačítka poznámka. */}
          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-slate-200">
            {uzka ? (
              <span className="text-[12px] leading-snug text-slate-500">Odpovědět na e-mail jde jen z počítače.</span>
            ) : (
              <button
                onClick={() => setReplyOpen(!replyOpen)}
                className="btn btn-primary"
              >
                <Send className="w-3.5 h-3.5" /> {replyOpen ? "Zavřít odpověď" : `Odpovědět z ${thread.account}`}
              </button>
            )}
          </div>

          {replyOpen && !uzka && detail && (
            <ReplyForm
              thread={thread}
              lastMessage={detail.messages[detail.messages.length - 1]}
              onClose={() => setReplyOpen(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

// BezpecnyHtml — vykreslí HTML cizího e-mailu do sandboxovaného iframe (bez skriptů, formulářů
// a přesměrování stránky). 27.10.2026 (bezpečnostní dávka 2): vytaženo z MessageView, aby stejným
// způsobem šla i citace původní zprávy u odpovědi (dřív se vkládalo jako holé HTML, tedy stored XSS).
// Výška iframe se přizpůsobí obsahu (max 800 px), stejně jako u hlavního zobrazení e-mailu.
function BezpecnyHtml({ html, title }) {
  const iframeRef = useRef(null);
  const htmlDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;font-size:14px;line-height:1.5;color:#1f2937;margin:0;padding:8px;word-wrap:break-word;}img{max-width:100%;height:auto;}blockquote{border-left:3px solid #e5e7eb;padding-left:12px;color:#6b7280;margin:8px 0;}a{color:#5B5296;}</style></head><body>${html}</body></html>`;

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const adjustHeight = () => {
      try {
        const h = iframe.contentDocument?.body?.scrollHeight || 400;
        iframe.style.height = `${Math.min(h + 20, 800)}px`;
      } catch { /* cross-origin or not ready */ }
    };
    iframe.addEventListener("load", adjustHeight);
    const timer = setTimeout(adjustHeight, 500);
    return () => { iframe.removeEventListener("load", adjustHeight); clearTimeout(timer); };
  }, [html]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={htmlDoc}
      sandbox="allow-same-origin allow-popups"
      className="w-full border-0 bg-white"
      style={{ minHeight: "100px" }}
      title={title}
    />
  );
}

// Prostý text e-mailu do HTML pro BezpecnyHtml: nejdřív se escapuje, pak se zalomí řádky.
function textDoHtml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;")
    .replace(/\n/g, "<br>");
}

// MessageView — render single email message: HTML body (iframe sandbox) + attachments + headers.
// HTML iframe sandbox blokuje JS, formuláře a top-navigation → bezpečný render bez DOMPurify dependency.
// 21.5.2026: collapsed mode pro dlouha vlakna. V tom rezimu jen header + 1 radek snippet, klik rozbali.
// 21.5.2026 fix: VSECHNY hooks musi byt PRED jakymkoli conditional return (rules-of-hooks).
//   Predchozi verze mela useEffect po `if (collapsed) return ...`, coz pri toggle
//   collapsed → expanded zpusobilo white screen (React detekoval mismatched hook count).
// 21.5.2026: default HTML view (formatovany email vetsinou citelnejsi).
//   Toggle na Text je 1 klik vpravo nahore.
function MessageView({ msg, account, collapsed = false, onToggleCollapse, isCollapsibleContext = false }) {
  const uzka = useUzkaObrazovka();
  const [showHtml, setShowHtml] = useState(true); // default HTML view — formatovany email
  const hasHtml = !!msg.body_html;
  const hasAttachments = msg.attachments && msg.attachments.length > 0;

  // Collapsed rendering — kompaktni header bez body. Hooks uz volane vyse.
  if (collapsed && isCollapsibleContext) {
    return (
      <button
        onClick={onToggleCollapse}
        className="w-full text-left rounded-lg border border-slate-200 bg-white px-3 py-2 hover:border-plum-300 hover:bg-plum-50/30 transition-colors group min-h-[44px] sm:min-h-0"
      >
        <div className="flex items-baseline gap-2">
          <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-plum-500 flex-shrink-0" />
          <span className="text-sm font-medium text-slate-800 truncate flex-1 min-w-0">{msg.from}</span>
          <span className="text-xs text-slate-500 whitespace-nowrap flex-shrink-0">{formatDateTime(msg.date_ts)}</span>
        </div>
        <div className="text-xs text-slate-500 truncate mt-0.5 pl-6">
          {msg.snippet || "(prázdná zpráva)"}
        </div>
        {hasAttachments && (
          <div className="text-[11px] text-slate-400 pl-6 mt-0.5 inline-flex items-center gap-1">
            <Paperclip className="w-3 h-3" /> {msg.attachments.length} {msg.attachments.length === 1 ? "příloha" : msg.attachments.length < 5 ? "přílohy" : "příloh"}
          </div>
        )}
      </button>
    );
  }

  return (
    <div className={`rounded-lg border bg-white px-3 py-2.5 ${msg.unread ? "border-plum-300" : "border-slate-200"}`}>
      {/* 9.9.2026 — na telefonu je jméno a čas na prvním řádku, adresy drobně pod tím
          a přepínač Text/HTML přes celou šířku. */}
      {uzka && (
        <div className="mb-2">
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-[14px] font-bold text-slate-900">{msg.from}</span>
            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-500">{formatDateTime(msg.date_ts)}</span>
          </div>
          <div className="mt-0.5 truncate text-[12px] text-slate-500">→ {msg.to}</div>
          {hasHtml && (
            <div className="mt-2 grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
              <button onClick={() => setShowHtml(false)}
                className={`flex min-h-[36px] items-center justify-center gap-1.5 rounded-xl text-[13px] font-bold transition-colors ${!showHtml ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>
                <FileText className="h-4 w-4" /> Text
              </button>
              <button onClick={() => setShowHtml(true)}
                className={`flex min-h-[36px] items-center justify-center gap-1.5 rounded-xl text-[13px] font-bold transition-colors ${showHtml ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>
                <Mail className="h-4 w-4" /> HTML
              </button>
            </div>
          )}
        </div>
      )}
      <div className={`${uzka ? "hidden" : "flex"} items-start justify-between gap-2 flex-wrap mb-2`}>
        <div className="text-sm text-slate-700 min-w-0 flex-1">
          <div className="font-semibold text-slate-900 truncate">{msg.from}</div>
          <div className="text-xs text-slate-500 truncate">→ {msg.to}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Vetsi segmented toggle Text / HTML pro lepsi viditelnost zamestnancum */}
          {hasHtml && (
            <div
              className="inline-flex items-center bg-slate-100 rounded-lg p-0.5 border border-slate-200"
              title="Přepínač zobrazení emailu"
            >
              <button
                onClick={() => setShowHtml(false)}
                className={`px-2.5 py-1 text-xs font-medium rounded transition-colors inline-flex items-center gap-1 ${
                  !showHtml
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
                title="Pouze text, lepší pro orientaci"
              >
                <FileText className="w-3.5 h-3.5" /> Text
              </button>
              <button
                onClick={() => setShowHtml(true)}
                className={`px-2.5 py-1 text-xs font-medium rounded transition-colors inline-flex items-center gap-1 ${
                  showHtml
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
                title="Plně formátovaný HTML email"
              >
                <Mail className="w-3.5 h-3.5" /> HTML
              </button>
            </div>
          )}
          <span className="text-xs text-slate-500 whitespace-nowrap">{formatDateTime(msg.date_ts)}</span>
        </div>
      </div>

      {/* Body — HTML iframe nebo plain text fallback */}
      {hasHtml && showHtml ? (
        <BezpecnyHtml html={msg.body_html} title={`Email body ${msg.id}`} />
      ) : (
        <div className="text-sm text-slate-800 whitespace-pre-wrap break-words leading-relaxed">
          {stripEmailQuotes(msg.body_plain) || msg.snippet || "(prázdná zpráva)"}
        </div>
      )}

      {/* Attachments */}
      {hasAttachments && (
        <div className="mt-2 pt-2 border-t border-slate-100">
          <div className="text-[11px] font-semibold text-slate-600 mb-1 flex items-center gap-1">
            <Paperclip className="w-3 h-3" /> Přílohy ({msg.attachments.length})
          </div>
          <div className="space-y-1">
            {msg.attachments.map((att, idx) => (
              <AttachmentItem
                key={idx}
                attachment={att}
                account={account}
                messageId={msg.id}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// AttachmentItem — link na download. Žluté/zelené/modré badge podle typu.
function AttachmentItem({ attachment, account, messageId }) {
  const sizeKb = Math.round((attachment.size || 0) / 1024);
  const isImage = (attachment.mime_type || "").startsWith("image/");
  const isPdf = attachment.mime_type === "application/pdf";
  const isOffice = /(word|excel|spreadsheet|presentation|document)/i.test(attachment.mime_type || "");

  const url = `/api/client-email-attachment?` + new URLSearchParams({
    account,
    message_id: messageId,
    attachment_id: attachment.attachment_id,
    filename: attachment.filename,
    mime_type: attachment.mime_type,
  }).toString();

  const iconColor = isImage ? "text-emerald-600" : isPdf ? "text-rose-600" : isOffice ? "text-blue-600" : "text-slate-500";

  return (
    <a
      href={url}
      download={attachment.filename}
      className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors text-xs"
    >
      <FileText className={`w-4 h-4 flex-shrink-0 ${iconColor}`} />
      <span className="flex-1 truncate font-medium text-slate-800">{attachment.filename}</span>
      <span className="text-slate-500 whitespace-nowrap">{sizeKb} KB</span>
      <Download className="w-3.5 h-3.5 text-slate-400" />
    </a>
  );
}

// Reply form — rich text editor (contentEditable) + draft/send options.
// 21.5.2026 polish:
//   - editor min-h zvetsen z 120px na 200px (vetsi prostor pro psani).
//   - automaticka citace puvodni zpravy collapsible block pod editorem (default sbalena).
//     Pri odeslani se citace appendne k body_html jako blockquote (standardni email format).
//   - Ctrl+Enter / Cmd+Enter shortcut pro Send.
//   - Send tlacitko velke primary, Draft male secondary.
function ReplyForm({ thread, lastMessage, onClose }) {
  const editorRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [sendMode, setSendMode] = useState(null); // "draft" | "send" | null
  const [showQuote, setShowQuote] = useState(false); // citace puvodni zpravy default sbalena

  // Default reply-to email (autor poslední zprávy)
  const replyTo = (() => {
    const m = (lastMessage?.from || "").match(/<([^>]+)>/);
    return m ? m[1] : (lastMessage?.from || "");
  })();

  // Citace puvodni zpravy. HTML format pro body_html, plain pro fallback.
  // Standard email format: "Dne {datum}, {from} napsal(a): [blockquote]"
  const quoteData = useMemo(() => {
    if (!lastMessage) return { html: "", plain: "" };
    const dateStr = lastMessage.date_ts
      ? new Date(lastMessage.date_ts).toLocaleString("cs-CZ", {
          day: "numeric", month: "numeric", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        })
      : "(neznámé datum)";
    const fromStr = lastMessage.from || "(neznámý odesílatel)";
    const originalHtml = lastMessage.body_html || "";
    const originalPlain = lastMessage.body_plain || lastMessage.snippet || "";

    // HTML citace pres blockquote (Gmail-friendly format)
    const html = `<br><br><div style="color:#6b7280;font-size:13px;">Dne ${dateStr}, ${fromStr} napsal(a):</div><blockquote style="margin:8px 0;padding-left:12px;border-left:3px solid #e5e7eb;color:#374151;">${originalHtml || originalPlain.replace(/\n/g, "<br>")}</blockquote>`;

    // Plain citace pres "> " prefix kazdy radek
    const plainQuoted = originalPlain.split("\n").map((l) => `> ${l}`).join("\n");
    const plain = `\n\nDne ${dateStr}, ${fromStr} napsal(a):\n${plainQuoted}`;

    return { html, plain, dateStr, fromStr, originalHtml, originalPlain };
  }, [lastMessage]);

  const execCmd = (cmd, value = null) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(cmd, false, value);
  };

  const handleSubmit = async (mode) => {
    const editor = editorRef.current;
    if (!editor) return;
    const userHtml = editor.innerHTML.trim();
    const userPlain = editor.innerText.trim();
    if (!userPlain) { setErr("Tělo zprávy je prázdné."); return; }
    if (mode === "send" && !await confirmDialog(`Opravdu odeslat email z ${thread.account_email} pro ${replyTo}?`)) return;
    setSending(true); setSendMode(mode); setErr(null);
    try {
      // 21.5.2026: append citace puvodni zpravy. Uzivatel ji nevidi v editoru
      // (clean psaci plocha), ale prijemce ji uvidi jako standardni blockquote.
      const bodyHtml = userHtml + (quoteData.html || "");
      const bodyPlain = userPlain + (quoteData.plain || "");

      const r = await fetch("/api/client-email-reply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          account: thread.account,
          thread_id: thread.thread_id,
          to: replyTo,
          subject: thread.subject,
          body: bodyPlain,
          body_html: bodyHtml,
          send: mode === "send",
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setResult(d);
    } catch (e) { setErr(e.message); }
    finally { setSending(false); }
  };

  // Ctrl+Enter / Cmd+Enter v editoru = odeslat ihned.
  const handleEditorKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      if (!sending) handleSubmit("send");
    }
  };

  const addLink = () => {
    const url = prompt("URL odkazu:");
    if (url) execCmd("createLink", url);
  };

  return (
    <div className="bg-white rounded-lg border border-plum-200 p-3 space-y-3 shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm text-slate-700">
          <div className="flex items-center gap-1.5 font-medium">
            <Send className="w-4 h-4 text-plum-600" /> Nová odpověď
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Z <strong className="text-slate-700">{thread.account_email}</strong> pro <strong className="text-slate-700">{replyTo}</strong>
          </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 flex-shrink-0" title="Zavřít odpověď">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-1 border-b border-slate-200 pb-2">
        <button onClick={() => execCmd("bold")} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Tučně (Ctrl+B)"><Bold className="w-4 h-4" /></button>
        <button onClick={() => execCmd("italic")} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Kurzíva (Ctrl+I)"><Italic className="w-4 h-4" /></button>
        <button onClick={() => execCmd("insertUnorderedList")} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Odrážky"><List className="w-4 h-4" /></button>
        <button onClick={addLink} className="p-1.5 rounded hover:bg-slate-100 text-slate-600 min-h-[44px] sm:min-h-0" title="Vložit odkaz"><LinkIcon className="w-4 h-4" /></button>
        <div className="flex-1" />
        <span className="text-[11px] text-slate-400 hidden sm:inline">Ctrl+Enter = Odeslat</span>
      </div>

      {/* Rich text editor (vetsi prostor: min-h 200px) */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onKeyDown={handleEditorKeyDown}
        className="w-full min-h-[200px] text-sm border border-slate-300 rounded-lg px-3 py-2.5 focus:outline-none focus:border-plum-500 focus:ring-1 focus:ring-plum-200 prose-sm max-w-none leading-relaxed"
        style={{ wordBreak: "break-word" }}
        placeholder="Napiš svou odpověď..."
      />

      {/* Citace puvodni zpravy — collapsible, default sbalena */}
      {(quoteData.originalHtml || quoteData.originalPlain) && (
        <div className="border border-slate-200 rounded-lg bg-slate-50 overflow-hidden">
          <button
            onClick={() => setShowQuote(!showQuote)}
            className="w-full text-left px-3 py-2 text-xs text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1.5"
          >
            {showQuote ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            <span className="font-medium">Citace původní zprávy</span>
            <span className="text-slate-400">Dne {quoteData.dateStr}, {quoteData.fromStr}</span>
            <span className="ml-auto text-slate-400 text-[11px]">automaticky se přidá k odeslanému emailu</span>
          </button>
          {showQuote && (
            <div className="px-3 py-2 border-t border-slate-200 bg-white max-h-[200px] overflow-y-auto">
              <div className="text-xs text-slate-700 pl-3 border-l-2 border-slate-300">
                <BezpecnyHtml
                  html={quoteData.originalHtml || textDoHtml(quoteData.originalPlain)}
                  title="Citace původní zprávy"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {err && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5">
          <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {err}
        </div>
      )}

      {result ? (
        <div className={`border rounded-lg px-3 py-2.5 text-sm space-y-1.5 ${result.sent ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-blue-50 border-blue-200 text-blue-900"}`}>
          <div className="font-semibold">
            {result.sent ? "Email odeslán" : "Draft vytvořen v Gmail"}
          </div>
          {!result.sent && <div className="text-xs">Otevři Gmail a zkontroluj draft, potom odešli ručně.</div>}
          <a
            href={result.gmail_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-1.5 bg-slate-700 hover:bg-slate-800 text-white rounded-lg text-xs font-medium"
          >
            <ExternalLink className="w-3.5 h-3.5" /> {result.sent ? "Otevřít odeslané" : "Otevřít draft v Gmail"}
          </a>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          {/* Send — primary big button */}
          <button
            onClick={() => handleSubmit("send")}
            disabled={sending}
            className="btn btn-primary"
            title="Odeslat email ihned (Ctrl+Enter)"
          >
            {sending && sendMode === "send" ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Odesílám…</>
            ) : (
              <><Send className="w-4 h-4" /> Odeslat</>
            )}
          </button>

          {/* Draft — secondary small button */}
          <button
            onClick={() => handleSubmit("draft")}
            disabled={sending}
            className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50"
            title="Vytvořit draft v Gmail, odešleš ručně po kontrole"
          >
            {sending && sendMode === "draft" ? (
              <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Vytvářím…</>
            ) : (
              <><FileText className="w-3.5 h-3.5" /> Uložit jako draft</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
